/**
 * K2 POS design tokens — transcribed from design_handoff_k2_pos/README.md.
 * Treat this file as the single source of truth; screens must not hard-code hexes.
 *
 * This is the web port of `apps/mobile/src/theme/tokens.ts`. Where that file
 * carried an approximation because React Native has no CSS equivalent, this one
 * carries the handoff's literal value — the departures are called out inline so
 * nobody "restores" the approximation later.
 *
 * `vite-plugins/tokens-css.ts` reads this module at build time and emits every
 * value below as a CSS custom property. Stylesheets use `var(--k2-*)`; this
 * module is imported directly only where JavaScript genuinely needs the value
 * (SVG fills, canvas, inline gradients computed from data).
 */

export const colors = {
  /** Brand navy — primary surfaces and dark chrome. */
  navy: '#0B3B86',
  navyDeep: '#0A2140',
  navyMid: '#1E5FB8',
  navyBright: '#2E76D6',
  /** Inner disc of the hero progress ring. */
  navyRing: '#123f8e',
  /** Hairlines and tracks on dark surfaces. */
  navyLine: '#2C4C82',

  /** Accent sky — the single accent; there is deliberately no amber in this system. */
  sky: '#38BDF8',
  skyDeep: '#0284C7',
  skyTint: '#E0F2FE',

  pageBg: '#E8F0FD',
  surface: '#FFFFFF',
  /** Light surface used on dark screens (button labels, sheet backgrounds). */
  surfaceAlt: '#F5F9FF',
  border: '#D3E1F6',
  borderMid: '#A9C0E4',

  text: '#12253F',
  textMuted: '#55688A',
  textFaint: '#90A6C6',

  /** Text tiers for use on navy. */
  onDark: '#E8F0FD',
  onDarkSubtle: '#B9CCEA',
  onDarkSoft: '#BBD4F5',
  onDarkBright: '#CFE2FB',
  onDarkFaint: '#7FA8DE',
} as const;

/**
 * Full CSS gradient values, straight from the handoff.
 *
 * The React Native tokens stored these as colour arrays plus separate location
 * arrays because `expo-linear-gradient` takes stops that way and cannot express
 * an arbitrary angle — `150deg` had to be approximated with start/end points.
 * On the web the spec is expressible verbatim, so it is.
 */
export const gradients = {
  splash: 'linear-gradient(150deg,#0A2140 0%,#0B3B86 55%,#1E5FB8 100%)',
  hero: 'linear-gradient(140deg,#0B3B86,#1E5FB8 60%,#2E76D6)',
  primaryButton: 'linear-gradient(135deg,#0B3B86,#1E5FB8)',
  logo: 'linear-gradient(135deg,#123f8e,#2E76D6)',
  /** 45° hazard stripes — the construction motif. Splash edges, 14px tall. */
  hazard: 'repeating-linear-gradient(-45deg, transparent 0 10px, rgba(56,189,248,.4) 10px 20px)',
  /** The tighter, fainter variant used on the debt hero's top-right corner. */
  hazardSoft:
    'repeating-linear-gradient(-45deg, transparent 0 9px, rgba(56,189,248,.35) 9px 18px)',
} as const;

/**
 * The hero progress ring. The handoff specifies a `conic-gradient`; React Native
 * has none, so the mobile app faked it with a stroked SVG circle. Here it is the
 * real thing, parameterised by progress.
 */
export function progressRing(pct: number): string {
  const p = Math.max(0, Math.min(100, pct));
  return `conic-gradient(${colors.sky} 0% ${p}%, rgba(255,255,255,.16) ${p}% 100%)`;
}

export const radius = {
  tile: 8,
  tileLg: 11,
  card: 14,
  hero: 18,
  pill: 20,
  button: 13,
  stepper: 8,
} as const;

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
} as const;

/**
 * Font families. Lao/UI text uses Noto Sans Lao; every money figure, date, ID and
 * micro-label uses Space Mono — that pairing is what makes the product feel like a POS.
 *
 * React Native needs one family name per weight; CSS needs one family and a
 * numeric `font-weight`, so the per-weight family map collapses to this. The
 * weights actually shipped are listed in `laoWeights` / `monoWeights` — they
 * drive both the `@font-face` block and the subsetting script, so adding a
 * weight here without adding the woff2 silently falls back to a synthesised
 * bold, which wrecks Lao tone-mark spacing.
 */
export const fonts = {
  lao: "'Noto Sans Lao', sans-serif",
  mono: "'Space Mono', ui-monospace, monospace",
} as const;

/** 600 existed in the RN tokens but was never used by a screen; it is not shipped. */
export const laoWeights = [400, 500, 700, 800, 900] as const;
export const monoWeights = [400, 700] as const;

export type LaoWeight = (typeof laoWeights)[number];
export type MonoWeight = (typeof monoWeights)[number];

/**
 * Global type scale. The handoff's sizes were drawn against a 340px-wide phone
 * mock and read too small on a real handset — Lao especially, where tone marks
 * above and vowel signs below eat into the apparent x-height, so a Lao glyph
 * needs more points than Latin to feel the same size.
 *
 * Every screen goes through `Text`/`Mono`, so this one multiplier lifts the
 * whole app and keeps the handoff's relative hierarchy intact. Sizes stay
 * written as the spec's numbers at each call site; only this converts them.
 *
 * On the web it ships as the `--k2-type-scale` custom property, which means a
 * media query can rescale the entire app for a desktop browser in one line —
 * see `global.css`. Do not scale text any other way.
 */
export const TYPE_SCALE = 1.15;

export function fontSize(size: number): number {
  return Math.round(size * TYPE_SCALE * 10) / 10;
}

/**
 * Elevation, verbatim from the handoff's Shadows line.
 *
 * The RN tokens carried approximations with a note that React Native has no
 * box-shadow spread/offset parity. These are the specified values; the negative
 * spread is exactly the part RN could not express, and it is what keeps the
 * shadow tight under the card instead of haloing around it.
 */
export const shadows = {
  hero: '0 16px 34px -18px rgba(11,59,134,.75)',
  button: '0 10px 22px -14px rgba(11,59,134,.9)',
  card: '0 6px 16px -12px rgba(11,59,134,.4)',
} as const;

/** Height of the custom bottom tab bar, excluding the safe-area inset. */
export const TAB_BAR_HEIGHT = 58;

/** Lao stacks marks above and below the baseline; too tight a leading clips them. */
export const LINE_HEIGHT_LAO = 1.45;
